import { ofType } from 'redux-observable';
import type { Observable } from 'rxjs';
import { bufferTime, catchError, defer, filter, from, groupBy, map, mergeMap, of, switchMap, timer } from 'rxjs';

import type { ContextOptionsAction, ContextTargetsAction } from '#actions/contexts';
import {
  loadContextOptionsFailed,
  loadContextTargetBatchDone,
  loadContextTargetBatchFailed,
  receiveContextOptions,
} from '#actions/contexts';
import { fetchContextList, fetchContextsWithNames, resolveContexts } from '#components/Contexts/common/contextAPI';
import type { ResolveContextsResponse } from '#components/Contexts/types';
import {
  augmentResolveContextsResponse,
  getContextsWithMissingNames,
  monkeyPatchContextItemsWithNames,
} from '#components/Contexts/utils/contextTargetingUtils';
import { epicRegistry as registry } from '#epics/registry';
import { DEBOUNCE_MS } from '#utils/inputUtils';

const RESOLVE_TARGETS_MAX_LIMIT = 50;

// MIGRATION — flag: `migrate-contexts-epic-to-react-query` (dogfood: migrateContextsEpicToReactQuery).
// These epics are being retired in favor of React Query reads and are on the flag-OFF path only.
// The flag-ON path resolves context data directly, with no epic/reducer round-trip:
//   - loadContextTargetBatch (contexts/LOAD_TARGET)     -> useContextData / useVisibleContextNames (@gonfalon/contexts)
//   - fetchContextOptions    (contexts/SEARCH_CONTEXTS) -> getContextsQuery (@gonfalon/rest-api) in SelectContextContainer
// Consumers wired behind the flag: ContextTarget, ContextTargetList, TargetEntryContainer, SelectContextContainer.
// When the flag reaches 100% and is removed, delete this file (and its registry.addEpics call below),
// the `#actions/contexts` action creators, and the `contextsByProjectAndEnvironmentKey` reducer + selectors.
export function loadContextTargetBatch(action$: Observable<ContextTargetsAction>) {
  return action$.pipe(
    ofType<ContextTargetsAction, 'contexts/LOAD_TARGET'>('contexts/LOAD_TARGET'),
    groupBy((action) => action.viewId),
    mergeMap((group) =>
      group.pipe(
        bufferTime(DEBOUNCE_MS, null, RESOLVE_TARGETS_MAX_LIMIT),
        filter((buffer) => buffer.length > 0),
        mergeMap((buffer) => {
          const viewId = group.key;
          const targets = buffer.map((action) => action.target);
          return defer(() => resolveContexts(buffer[0].projKey, buffer[0].envKey, targets, '-ts')).pipe(
            switchMap((contextsResponse: ResolveContextsResponse) => {
              const missingContextNames = getContextsWithMissingNames(contextsResponse);

              if (!missingContextNames.length) {
                return of(
                  loadContextTargetBatchDone(viewId, targets, contextsResponse, buffer[0].projKey, buffer[0].envKey),
                );
              }

              return from(fetchContextsWithNames(buffer[0].projKey, buffer[0].envKey, missingContextNames)).pipe(
                map((contextsResponseWithNames) => {
                  if (contextsResponseWithNames.items.length === 0) {
                    return loadContextTargetBatchDone(
                      viewId,
                      targets,
                      contextsResponse,
                      buffer[0].projKey,
                      buffer[0].envKey,
                    );
                  } else {
                    const contextsWithNames = monkeyPatchContextItemsWithNames(
                      contextsResponse,
                      contextsResponseWithNames,
                    );
                    const entitesWithNames = augmentResolveContextsResponse(contextsWithNames);
                    return loadContextTargetBatchDone(
                      viewId,
                      targets,
                      entitesWithNames,
                      buffer[0].projKey,
                      buffer[0].envKey,
                    );
                  }
                }),
              );
            }),
            catchError((error) =>
              of(loadContextTargetBatchFailed(error, viewId, targets, buffer[0].projKey, buffer[0].envKey)),
            ),
          );
        }),
      ),
    ),
  );
}

export function fetchContextOptions(action$: Observable<ContextOptionsAction>) {
  return action$.pipe(
    ofType<ContextOptionsAction, 'contexts/SEARCH_CONTEXTS'>('contexts/SEARCH_CONTEXTS'),
    groupBy((action) => action.id),
    mergeMap((group) =>
      group.pipe(
        switchMap((action) =>
          timer(DEBOUNCE_MS).pipe(
            mergeMap(() => {
              const { projKey: projectKey, envKey: environmentKey, contextKind: kind, q: prefix } = action;
              return defer(() => fetchContextList(projectKey, environmentKey, kind, prefix)).pipe(
                switchMap((contextsResponse: ResolveContextsResponse) => {
                  const missingContextNames = getContextsWithMissingNames(contextsResponse);

                  if (!missingContextNames.length) {
                    return of(
                      receiveContextOptions(action.id, action.q, contextsResponse, projectKey, environmentKey, kind),
                    );
                  }

                  return from(fetchContextsWithNames(projectKey, environmentKey, missingContextNames)).pipe(
                    map((contextsResponseWithNames) => {
                      if (contextsResponseWithNames.items.length === 0) {
                        return receiveContextOptions(
                          action.id,
                          action.q,
                          augmentResolveContextsResponse(contextsResponse),
                          projectKey,
                          environmentKey,
                          kind,
                        );
                      } else {
                        const contextsWithNames = monkeyPatchContextItemsWithNames(
                          contextsResponse,
                          contextsResponseWithNames,
                        );
                        return receiveContextOptions(
                          action.id,
                          action.q,
                          augmentResolveContextsResponse(contextsWithNames),
                          projectKey,
                          environmentKey,
                          kind,
                        );
                      }
                    }),
                  );
                }),
                catchError((error) =>
                  of(loadContextOptionsFailed(error, action.id, action.q, projectKey, environmentKey, kind)),
                ),
              );
            }),
          ),
        ),
      ),
    ),
  );
}

// See the MIGRATION note above: this registration (and these epics) is removed once
// `migrate-contexts-epic-to-react-query` is fully rolled out.
registry.addEpics([loadContextTargetBatch, fetchContextOptions]);
